import React, { useEffect, useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
} from '@mui/material';
import client from '../../../../../API';

/* Header surface */
const headerSx = {
  '& th': { bgcolor: 'grey.100', color: 'text.primary', fontWeight: 600 },
};

/* Parse either "YYYY-MM-DD" or "YYYY-MM-DD – YYYY-MM-DD" */
const toDate = (weekStr) => new Date(weekStr.split(/[–-]/)[0].trim());

const CampaignsTable = ({ setCampaignKpi, delay = 0 }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isReady, setIsReady] = useState(false);

  // Stagger loading with delay
  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  // Use useQuery for proper caching (replaces useMutation)
  const { data, isLoading, isError, error } = useQuery(
    ['campaigns-kpi'],
    client.campaigns.listKPI,
    {
      enabled: isReady,  // Only fetch when delay has passed
      staleTime: 10 * 60 * 1000,  // 10 minutes (matches backend cache)
      cacheTime: 15 * 60 * 1000,  // 15 minutes
      refetchOnWindowFocus: false
    }
  );

  // Update parent component when data changes
  useEffect(() => {
    if (data) {
      const [latest] = Object.keys(data).sort((a, b) => toDate(b) - toDate(a));
      const total =
        (data[latest]?.Draft || 0) +
        (data[latest]?.Launched || 0) +
        (data[latest]?.Completed || 0);
      setCampaignKpi({ progress: total });
    }
  }, [data, setCampaignKpi]);

  /* memoised, robust recency sort */
  const sorted = useMemo(
    () =>
      data ? Object.entries(data).sort(
        (a, b) => toDate(b[0]) - toDate(a[0])
      ) : [],
    [data]
  );

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error: {error.message}</div>;
  if (!data) return <div>Loading...</div>;

  return (
    <Paper elevation={2}>
      <TableContainer>
        <Table stickyHeader>
          <TableHead sx={headerSx}>
            <TableRow>
              <TableCell>Week Starting</TableCell>
              <TableCell>Draft</TableCell>
              <TableCell>Launched</TableCell>
              <TableCell>Completed</TableCell>
              <TableCell>Total $</TableCell>
              <TableCell>Avg Completion (Days)</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sorted
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map(([week, d]) => (
                <TableRow key={week}>
                  <TableCell>{week}</TableCell>
                  <TableCell>{d.Draft}</TableCell>
                  <TableCell>{d.Launched}</TableCell>
                  <TableCell>{d.Completed}</TableCell>
                  <TableCell>${d.total_sum?.toFixed(2) || '0.00'}</TableCell>
                  <TableCell>
                    {d.avg_completion_time
                      ? d.avg_completion_time.toFixed(2)
                      : 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        rowsPerPageOptions={[5, 10, 25]}
        count={sorted.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Paper>
  );
};

export default CampaignsTable;
